import { useCopilotChat, useCopilotReadable } from "@copilotkit/react-core";
import { Role, TextMessage } from "@copilotkit/runtime-client-gql";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	type ChatContextType,
	type ChatState,
	initialChatState,
} from "./state";

type ChatActions = {
	setPartialChatState: (state: Partial<ChatState>) => void;
	pushContextMessage: (message: string) => void;
};

const ChatStateContext = createContext<ChatState>({} as ChatState);
const ChatActionsContext = createContext<ChatActions>({} as ChatActions);
const AnalyticsContext = createContext<{ analyticsEnabled: boolean }>({
	analyticsEnabled: false,
});

export const useRedeemCopilotState = () => useContext(ChatStateContext);
export const useRedeemCopilotActions = () => useContext(ChatActionsContext);
export const useRedeemCopilotAnalytics = () => useContext(AnalyticsContext);

export const useRedeemCopilot = (): ChatContextType => {
	const state = useRedeemCopilotState();
	const actions = useRedeemCopilotActions();
	const { analyticsEnabled } = useRedeemCopilotAnalytics();

	return {
		chatState: state,
		...actions,
		analyticsEnabled,
	};
};

export const ContextProvider = ({
	enableAnalytics = false,
	initialChatState: parsedInitialChatState,
	instructions,
	children,
}: {
	enableAnalytics?: boolean;
	initialChatState?: Partial<ChatState>;
	instructions?: string;
	children?: React.ReactNode;
}) => {
	const [chatState, setChatState] = useState<ChatState>({
		...initialChatState,
		...parsedInitialChatState,
	});

	const { chatOpen, userId, ...readableChatState } = chatState;

	useCopilotReadable({
		description: `The current chat state including username, userEmail,
		appName, appSupportEmail, assistantTitle and operational instructions.`,
		value: { chatState: readableChatState, instructions },
	});

	useEffect(() => {
		setChatState((prevState) => ({
			...prevState,
			...parsedInitialChatState,
		}));
	}, [parsedInitialChatState]);

	const { appendMessage } = useCopilotChat();

	const setPartialChatState = useCallback((state: Partial<ChatState>) => {
		setChatState((prevState) => ({
			...prevState,
			...state,
		}));
	}, []);

	const pushContextMessage = useCallback(
		(message: string) => {
			appendMessage(
				new TextMessage({
					role: Role.System,
					content: message,
				}),
			);
		},
		[appendMessage],
	);

	// Memoize action values to prevent unnecessary re-renders
	const actionValues = useMemo(
		() => ({
			setPartialChatState,
			pushContextMessage,
		}),
		[setPartialChatState, pushContextMessage],
	);

	// Memoize analytics value
	const analyticsValue = useMemo(
		() => ({
			analyticsEnabled: enableAnalytics,
		}),
		[enableAnalytics],
	);

	return (
		<AnalyticsContext.Provider value={analyticsValue}>
			<ChatActionsContext.Provider value={actionValues}>
				<ChatStateContext.Provider value={chatState}>
					{children}
				</ChatStateContext.Provider>
			</ChatActionsContext.Provider>
		</AnalyticsContext.Provider>
	);
};
